/** @jsxImportSource @emotion/react */
// NOTE: intentionally NOT using the React Compiler `'use memo'` directive here.
// This component needs an explicit, stably-identified `registerSectionChecked`
// callback (Section's registration effect depends on its identity — an unstable
// identity churns register/unregister and breaks the scroll-spy click lock), and
// it must be re-created only when the key set changes. The compiler can't model
// that custom dependency, so we manage memoization manually with useCallback.
// (Sidebar/Section keep `'use memo'` — they are pure-render with no such need.)

import css from '@emotion/css/macro'
import React, { useCallback, useRef } from 'react'
import { useScrollSpy } from './hooks/useScrollSpy'
import { ScrollSpyContext, useScrollSpyContext } from './ScrollSpyContext'
import type { ScrollSpyItem } from './types'
import Sidebar from './Sidebar'
import Section from './Section'

interface ScrollSpyLayoutProps {
  /** Ordered nav entries; their keys define section order + the default active. */
  items: ScrollSpyItem[]
  /** Horizontal gap between sidebar and content (px). Default 24 (Figma). */
  gap?: number
  /** Stack vertically (e.g. mobile widths). Default false. */
  stacked?: boolean
  children: React.ReactNode
}

const ScrollSpyLayoutRoot = ({
  items,
  gap = 24,
  stacked = false,
  children,
}: ScrollSpyLayoutProps) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const keys = items.map((i) => i.key)
  const keySet = new Set(keys)
  const { activeId, registerSection, scrollToSection } = useScrollSpy(
    keys,
    scrollContainerRef
  )

  // Stable identity: Section's registration effect depends on this, so a fresh
  // function each render would churn register/unregister and drop the scroll-spy
  // state. Read keys/keySet through refs and depend only on the (stable)
  // underlying registerSection plus the keys signature.
  const keysKey = keys.join(',')
  const keysRef = useRef(keys)
  keysRef.current = keys
  const keySetRef = useRef(keySet)
  keySetRef.current = keySet

  const registerSectionChecked = useCallback(
    (key: string, el: HTMLElement | null) => {
      if (
        process.env.NODE_ENV !== 'production' &&
        el &&
        !keySetRef.current.has(key)
      ) {
        console.warn(
          `ScrollSpyLayout: <Section id="${key}"> has no matching item key. ` +
            `Valid keys: ${keysRef.current.join(', ')}`
        )
      }
      registerSection(key, el)
    },
    // keysKey re-creates the checker only when the set/order of keys changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registerSection, keysKey]
  )

  return (
    <ScrollSpyContext.Provider
      value={{
        activeId,
        items,
        registerSection: registerSectionChecked,
        scrollToSection,
        scrollContainerRef,
      }}
    >
      <div
        css={css`
          ${bodyStyle}
          gap: ${gap}px;
          flex-direction: ${stacked ? 'column' : 'row'};
        `}
      >
        {children}
      </div>
    </ScrollSpyContext.Provider>
  )
}

/** Scrollable right-hand column. Attaches the scroll-spy container ref. */
const Content = ({
  children,
  ariaLabel = 'Section content',
  /** Right gutter (px) between the content and the scrollbar. Default 0. */
  paddingRight = 0,
}: {
  children: React.ReactNode
  ariaLabel?: string
  paddingRight?: number
}) => {
  const { scrollContainerRef } = useScrollSpyContext()
  return (
    <div
      css={css`
        ${contentAreaStyle}
        padding-right: ${paddingRight}px;
      `}
      ref={scrollContainerRef as React.RefObject<HTMLDivElement>}
      tabIndex={0}
      role='region'
      aria-label={ariaLabel}
    >
      {children}
    </div>
  )
}

type ScrollSpyLayoutComponent = typeof ScrollSpyLayoutRoot & {
  Sidebar: typeof Sidebar
  Content: typeof Content
  Section: typeof Section
}

const ScrollSpyLayout = ScrollSpyLayoutRoot as ScrollSpyLayoutComponent
ScrollSpyLayout.Sidebar = Sidebar
ScrollSpyLayout.Content = Content
ScrollSpyLayout.Section = Section

export default ScrollSpyLayout

// ---- styled ----
const bodyStyle = css`
  display: flex;
  flex: 1;
  overflow: hidden;
`

const contentAreaStyle = css`
  flex: 1;
  overflow-y: auto;
  /* Establish an inline-size query container so descendants (e.g. FieldRow) can
     collapse based on THIS column's real width — which shrinks when the app
     sidebar opens — instead of the viewport width, which can't see the sidebar. */
  container-type: inline-size;
`
